import { ConsentModel } from "@/api";
import { createConsentManagementConfigRequest } from "@/consent-management/api/createConsentManagementConfig";
import { ConsentManagementIntegration } from "@/consent-management/consentManagement";
import { defaultColorStyles } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/ConfigContent/FreshpaintConfigContent/sections/FreshpaintThemeConfig/constants";
import {
  ConsentManagerConfigSchema,
  defaultCategorySettings,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { useConsentCategorizableDestinations } from "@/consent-management/hooks/useConsentCategorizableDestinations";
import {
  buildFreshpaintCategories,
  sortPreferencesModalSections,
} from "@/consent-management/utils/freshpaint";

/**
 * The payload for creating an integration's config for the first time. It is
 * created disabled, so nothing changes for visitors until it is published and
 * turned on.
 */
export function buildCreateConsentConfigRequest({
  integration,
  consentModel,
  enabledCategorizableDestinations,
}: {
  integration: ConsentManagementIntegration;
  consentModel: ConsentModel;
  enabledCategorizableDestinations: ReturnType<
    typeof useConsentCategorizableDestinations
  >;
}): createConsentManagementConfigRequest {
  const consentMapping: Record<string, string> = {};
  for (const {
    saasDestination,
    destinationConfig,
  } of enabledCategorizableDestinations) {
    consentMapping[destinationConfig.name] =
      integration.getDefaultConsentCategoryForDest(saasDestination);
  }

  const payload: createConsentManagementConfigRequest = {
    type: integration.slug,
    enabled: false,
    consentModel,
    categoryMapping: integration.defaultConsentCategories,
    consentMapping,
  };
  if (integration.slug === "freshpaint") {
    payload.freshpaintConfig = buildInitialFreshpaintConfig(
      integration,
      payload.categoryMapping,
      consentModel,
    );
  }
  return payload;
}

function buildInitialFreshpaintConfig(
  integration: ConsentManagementIntegration,
  categoryMapping: Record<string, string>,
  consentModel: ConsentModel,
) {
  const categorySettings = defaultCategorySettings(categoryMapping);
  const categories = buildFreshpaintCategories(
    categoryMapping,
    consentModel,
    categorySettings,
  );

  const sections = sortPreferencesModalSections(
    Object.entries(integration.defaultConsentCategories).map(
      ([categoryKey, categoryLabel]) => {
        const friendlyName = categoryLabel || categoryKey;
        return {
          title: friendlyName,
          description: integration.defaultCategoryDescriptions[categoryKey],
          linkedCategory: categoryKey,
        };
      },
    ),
  );

  const config: NonNullable<ConsentManagerConfigSchema["freshpaintConfig"]> & {
    categories: unknown;
  } = {
    categories,
    categorySettings,
    autoShow: true,
    disablePageInteraction: false,
    hideConsentModalRejectAll: false,
    enableConsentModalDismiss: false,
    colorStyles: defaultColorStyles,
    editSelections: true,
    gcmEnabled: false,
    guiOptions: {
      consentModal: {
        layout: "box",
        position: "bottom right",
      },
      preferencesModal: {
        layout: "box",
        position: "right",
      },
    },
    iconColor: "#dedede",
    iconPosition: "right" as const,
    customCSS: "",
    simpleBackgroundColor: "#232323",
    simplePrimaryColor: "#c9bbbb",
    simpleSecondaryColor: "#dedede",
    geotargetingBehavior: "managed",
    privacyPolicyUrl: "",
    privacyPolicyLinkText: "",
    conditionalConfigs: [],
    language: {
      default: "en",
      autoDetect: "browser",
      translations: {
        en: {
          consentModal: {
            title: "We Value Your Privacy",
            description:
              'This website uses cookies and similar technologies to enhance your experience, analyze site performance, and deliver personalized content. By selecting "Accept Cookies" you agree to the storing of cookies on your device. By selecting "Reject Cookies" you agree to using only strictly necessary cookies on your device.',
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            showPreferencesBtn: "Manage Individual preferences",
          },
          preferencesModal: {
            title: "We Value Your Privacy",
            description: "",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            savePreferencesBtn: "Accept current selection",
            closeIconLabel: "Close modal",
            sections,
          },
        },
      },
    },
  };

  return config;
}
